/**
 * Fiche d'une organisation du répertoire commun (CDC §6.10, §5.1).
 *
 * Une entreprise, une association et un partenaire se saisissent ici sur **le
 * même formulaire** : mêmes coordonnées, même rattachement d'antennes, mêmes
 * contrôles. Seule la nature change ce qui s'ajoute — le SIRET et les données
 * d'employeur pour une entreprise, le RNA pour une association déclarée.
 *
 * La même fenêtre sert à la création et à la correction, comme la fiche de
 * recueil du besoin : deux formulaires divergents finiraient par ne plus
 * collecter la même chose.
 *
 * Une entreprise y reçoit aussi ses maîtres d'apprentissage (ticket du
 * 13/10/2026) : ceux qu'elle a déjà sont rappelés, d'autres s'ajoutent par
 * « + ». Ce sont les fiches que le contrat proposera ensuite au choix.
 *
 * La nature n'est pas modifiable après coup : entreprise et partenaire sont
 * deux tables — une entreprise a des contrats, un partenaire n'en a pas — et
 * transformer l'une en l'autre perdrait ce qui y est rattaché. En modification,
 * la nature est rappelée en lecture ; ce qui reste ajustable, c'est le statut
 * de relation d'une entreprise, ou le type d'un partenaire.
 */

import { useState } from 'react';
import { BoutonMint, BoutonSecondaire, MessageErreur } from '../ui/Primitives';
import { Modale } from '../ui/Modale';
import { CLASSE_CHAMP } from '../../lib/fiche-besoin';
import { Champ } from '../commercial/ChampsFicheBesoin';
import { SaisieMaitresApprentissage } from './SaisieMaitresApprentissage';
import {
  ligneMaitreVide,
  maitresSaisis,
  problemesMaitres,
  type LigneMaitre,
} from '../../lib/maitres-apprentissage';
import type {
  MaitreApprentissageLigne,
  ModificationEntreprise,
  SecteurEmployeur,
  StatutRelation,
} from '../../lib/useEntreprises';
import type {
  FicheOrganisation,
  GenreOrganisation,
  ModificationPartenaire,
} from '../../lib/usePedagogique';

/** Nature choisie à la création : elle décide de la table de destination. */
export type NatureOrganisation = 'ENTREPRISE' | string;

export interface SortieOrganisation {
  genre: GenreOrganisation;
  /**
   * Renseigné quand `genre` vaut ENTREPRISE. Le secteur employeur est toujours
   * porté — le formulaire coche l'un ou l'autre, il ne le laisse jamais vide.
   */
  entreprise?: ModificationEntreprise & {
    siret: string;
    denomination: string;
    sites: string[];
    secteurEmployeur: SecteurEmployeur;
  };
  /** Renseigné quand `genre` vaut PARTENAIRE. */
  partenaire?: ModificationPartenaire & { nom: string; type: string; sites: string[] };
}

interface Valeurs {
  nature: NatureOrganisation;
  statutRelation: string;
  nom: string;
  siret: string;
  rna: string;
  activite: string;
  adresse: string;
  codePostal: string;
  ville: string;
  telephone: string;
  email: string;
  siteWeb: string;
  contact: string;
  gerant: string;
  opco: string;
  idcc: string;
  effectif: string;
  numeroVoie: string;
  voie: string;
  complementAdresse: string;
  secteurEmployeur: SecteurEmployeur;
  typeEmployeur: string;
  employeurSpecifique: string;
  caisseRetraite: string;
  codeNaf: string;
  sites: string[];
  /** Maîtres d'apprentissage **à ajouter** ; les existants ne se saisissent pas. */
  maitres: LigneMaitre[];
}

const VIDE: Valeurs = {
  nature: 'ENTREPRISE',
  statutRelation: 'A_PROSPECTER',
  nom: '',
  siret: '',
  rna: '',
  activite: '',
  adresse: '',
  codePostal: '',
  ville: '',
  telephone: '',
  email: '',
  siteWeb: '',
  contact: '',
  gerant: '',
  opco: '',
  idcc: '',
  effectif: '',
  numeroVoie: '',
  voie: '',
  complementAdresse: '',
  secteurEmployeur: 'PRIVE',
  typeEmployeur: '',
  employeurSpecifique: '',
  caisseRetraite: '',
  codeNaf: '',
  sites: [],
  maitres: [],
};

/**
 * Valeurs de départ d'une correction.
 *
 * Les fiches créées avant le découpage CERFA n'ont qu'une ligne d'adresse
 * libre. Elle est reprise dans « Voie » : sans cela, le formulaire renverrait
 * un découpage vide et effacerait l'adresse à la première correction, sur une
 * fiche que personne n'a voulu toucher. La personne la redécoupe si elle veut,
 * et la ligne recomposée reste identique en attendant.
 */
function valeursDe(fiche: FicheOrganisation): Valeurs {
  const decoupageAbsent = !fiche.numeroVoie && !fiche.voie && !fiche.complementAdresse;
  const voieReprise =
    fiche.genre === 'ENTREPRISE' && decoupageAbsent ? fiche.adresse ?? '' : fiche.voie ?? '';

  return {
    nature: fiche.genre === 'ENTREPRISE' ? 'ENTREPRISE' : fiche.type,
    statutRelation: fiche.genre === 'ENTREPRISE' ? fiche.type : 'A_PROSPECTER',
    nom: fiche.nom,
    siret: fiche.siret ?? '',
    rna: fiche.rna ?? '',
    activite: fiche.activite ?? '',
    adresse: fiche.adresse ?? '',
    codePostal: fiche.codePostal ?? '',
    ville: fiche.ville ?? '',
    telephone: fiche.telephone ?? '',
    email: fiche.email ?? '',
    siteWeb: fiche.siteWeb ?? '',
    contact: fiche.contactOperationnel ?? fiche.contact ?? '',
    gerant: fiche.gerant ?? '',
    opco: fiche.opco ?? '',
    idcc: fiche.idcc ?? '',
    effectif: fiche.effectif === null ? '' : String(fiche.effectif),
    numeroVoie: fiche.numeroVoie ?? '',
    voie: voieReprise,
    complementAdresse: fiche.complementAdresse ?? '',
    secteurEmployeur: fiche.secteurEmployeur ?? 'PRIVE',
    typeEmployeur: fiche.typeEmployeur ?? '',
    employeurSpecifique: fiche.employeurSpecifique ?? '',
    caisseRetraite: fiche.caisseRetraite ?? '',
    codeNaf: fiche.codeNaf ?? '',
    sites: [...fiche.sites],
    maitres: [],
  };
}

const sansEspaces = (valeur: string): string => valeur.replace(/\s+/g, '');

/** Le point des copier-coller (`10.71C`) n'appartient pas au code NAF. */
const normaliserNaf = (valeur: string): string => valeur.replace(/[\s.]/g, '').toUpperCase();

/** Les mêmes bornes que l'API, pour ne pas faire l'aller-retour sur une évidence. */
function erreurs(valeurs: Valeurs): string[] {
  const liste: string[] = [];
  if (valeurs.nom.trim().length < 2) liste.push('Nom ou raison sociale trop court.');
  if (valeurs.nature === 'ENTREPRISE' && !/^\d{14}$/.test(sansEspaces(valeurs.siret))) {
    liste.push('SIRET attendu : 14 chiffres.');
  }
  if (
    valeurs.nature !== 'ENTREPRISE' &&
    valeurs.rna.trim() &&
    !/^W\d{9}$/.test(sansEspaces(valeurs.rna).toUpperCase())
  ) {
    liste.push('Numéro RNA attendu : W suivi de 9 chiffres.');
  }
  if (valeurs.codePostal.trim() && !/^\d{5}$/.test(valeurs.codePostal.trim())) {
    liste.push('Code postal attendu : 5 chiffres.');
  }
  if (valeurs.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(valeurs.email.trim())) {
    liste.push('Adresse e-mail invalide.');
  }
  if (valeurs.nature === 'ENTREPRISE') {
    if (valeurs.idcc.trim() && !/^\d{4}$/.test(valeurs.idcc.trim())) {
      liste.push('IDCC attendu : 4 chiffres.');
    }
    if (valeurs.effectif.trim() && !/^\d{1,7}$/.test(valeurs.effectif.trim())) {
      liste.push("L'effectif se saisit en nombre entier.");
    }
    if (valeurs.codeNaf.trim() && !/^\d{4}[A-Z]$/.test(normaliserNaf(valeurs.codeNaf))) {
      liste.push('Code NAF attendu : 4 chiffres et une lettre (1071C).');
    }
    liste.push(...problemesMaitres(valeurs.maitres));
  }
  if (valeurs.sites.length === 0) liste.push('Rattacher au moins un site.');
  return liste;
}

const texte = (valeur: string): string => valeur.trim();

/**
 * Retire les champs laissés vides d'une création.
 *
 * À la **correction**, un champ vidé part vide : c'est ainsi que l'API l'efface.
 * À la **création**, il n'y a rien à effacer — et le schéma de création refuse
 * une chaîne vide là où il attend un format (code postal, e-mail, IDCC, NAF).
 * Les envoyer quand même faisait échouer toute création dès qu'un champ
 * facultatif restait vide.
 */
function sansChampsVides<T extends Record<string, unknown>>(entree: T): T {
  return Object.fromEntries(
    Object.entries(entree).filter(([, valeur]) => valeur !== '' && valeur !== null),
  ) as T;
}

function versSortie(valeurs: Valeurs, creation: boolean): SortieOrganisation {
  const ajuster = <T extends Record<string, unknown>>(entree: T): T =>
    creation ? sansChampsVides(entree) : entree;

  if (valeurs.nature === 'ENTREPRISE') {
    const maitres = maitresSaisis(valeurs.maitres);
    return {
      genre: 'ENTREPRISE',
      entreprise: ajuster({
        siret: sansEspaces(valeurs.siret),
        denomination: texte(valeurs.nom),
        statutRelation: valeurs.statutRelation as StatutRelation,
        secteur: texte(valeurs.activite),
        numeroVoie: texte(valeurs.numeroVoie),
        voie: texte(valeurs.voie),
        complementAdresse: texte(valeurs.complementAdresse),
        codePostal: texte(valeurs.codePostal),
        ville: texte(valeurs.ville),
        telephone: texte(valeurs.telephone),
        email: texte(valeurs.email),
        siteWeb: texte(valeurs.siteWeb),
        contactOperationnel: texte(valeurs.contact),
        gerant: texte(valeurs.gerant),
        opco: texte(valeurs.opco),
        idcc: texte(valeurs.idcc),
        effectif: valeurs.effectif.trim() === '' ? null : Number(valeurs.effectif.trim()),
        secteurEmployeur: valeurs.secteurEmployeur,
        typeEmployeur: texte(valeurs.typeEmployeur),
        employeurSpecifique: texte(valeurs.employeurSpecifique),
        caisseRetraite: texte(valeurs.caisseRetraite),
        codeNaf: normaliserNaf(valeurs.codeNaf),
        sites: valeurs.sites,
        ...(maitres.length > 0 ? { maitresApprentissage: maitres } : {}),
      }),
    };
  }

  return {
    genre: 'PARTENAIRE',
    partenaire: ajuster({
      nom: texte(valeurs.nom),
      type: valeurs.nature,
      domaine: texte(valeurs.activite),
      adresse: texte(valeurs.adresse),
      codePostal: texte(valeurs.codePostal),
      ville: texte(valeurs.ville),
      telephone: texte(valeurs.telephone),
      email: texte(valeurs.email),
      siteWeb: texte(valeurs.siteWeb),
      contact: texte(valeurs.contact),
      rna: sansEspaces(valeurs.rna).toUpperCase(),
      sites: valeurs.sites,
    }),
  };
}

export function ModaleOrganisation({
  ouverte,
  fiche = null,
  natureImposee,
  statutsRelation,
  typesPartenaire,
  secteurs,
  typesEmployeur = [],
  employeursSpecifiques = [],
  sitesDisponibles,
  sitesParDefaut = [],
  maitresExistants = [],
  enCours,
  erreur,
  onFermer,
  onEnregistrer,
}: {
  ouverte: boolean;
  /** Fiche à corriger. `null` : c'est une création. */
  fiche?: FicheOrganisation | null;
  /** Force la nature à la création, quand l'écran ne crée qu'un genre. */
  natureImposee?: NatureOrganisation;
  statutsRelation: { valeur: StatutRelation; libelle: string }[];
  typesPartenaire: { valeur: string; libelle: string }[];
  secteurs: string[];
  /** Natures juridiques suggérées pour la fiche employeur (CDC §4.8). */
  typesEmployeur?: string[];
  /** « Employeur spécifique » du CERFA : liste ouverte, à confirmer. */
  employeursSpecifiques?: string[];
  sitesDisponibles: string[];
  /** Antennes cochées d'office à la création (le périmètre courant). */
  sitesParDefaut?: string[];
  /** Maîtres d'apprentissage déjà mémorisés de l'entreprise corrigée. */
  maitresExistants?: readonly MaitreApprentissageLigne[];
  enCours: boolean;
  erreur: string | null;
  onFermer: () => void;
  onEnregistrer: (sortie: SortieOrganisation) => void;
}): JSX.Element | null {
  const modification = fiche !== null;
  const [valeurs, setValeurs] = useState<Valeurs>(() => ({
    ...(fiche
      ? valeursDe(fiche)
      : { ...VIDE, nature: natureImposee ?? 'ENTREPRISE', sites: [...sitesParDefaut] }),
    // Une ligne prête à remplir tant que l'entreprise n'a aucun maître ;
    // sinon, « + » en ajoute une à la demande.
    maitres: maitresExistants.length === 0 ? [ligneMaitreVide()] : [],
  }));

  if (!ouverte) return null;

  const modifier = <C extends keyof Valeurs>(champ: C, valeur: Valeurs[C]): void =>
    setValeurs((actuelles) => ({ ...actuelles, [champ]: valeur }));

  const problemes = erreurs(valeurs);
  const entreprise = valeurs.nature === 'ENTREPRISE';

  const basculerSite = (code: string): void =>
    setValeurs((actuelles) => ({
      ...actuelles,
      sites: actuelles.sites.includes(code)
        ? actuelles.sites.filter((valeur) => valeur !== code)
        : [...actuelles.sites, code],
    }));

  return (
    <Modale
      ouverte
      titre={modification ? 'Modifier les informations' : 'Ajouter un contact'}
      sousTitre={
        modification
          ? fiche.nom
          : 'Entreprise, association ou partenaire : la même fiche, une nature différente'
      }
      onFermer={onFermer}
      largeur="max-w-3xl"
    >
      <form
        className="space-y-5 p-4 sm:p-6"
        onSubmit={(evenement) => {
          evenement.preventDefault();
          if (enCours || problemes.length > 0) return;
          onEnregistrer(versSortie(valeurs, !modification));
        }}
      >
        <Section titre="Nature">
          <Champ libelle="Nature de l’organisation">
            {modification || natureImposee ? (
              <div className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm text-codialis-navy">
                {entreprise
                  ? 'Entreprise'
                  : typesPartenaire.find((type) => type.valeur === valeurs.nature)?.libelle ??
                    valeurs.nature}
                {modification && (
                  <span className="ml-2 text-xs text-codialis-gray">
                    · non modifiable après création
                  </span>
                )}
              </div>
            ) : (
              <select
                value={valeurs.nature}
                onChange={(evenement) => modifier('nature', evenement.target.value)}
                className={CLASSE_CHAMP}
              >
                <option value="ENTREPRISE">Entreprise</option>
                {typesPartenaire.map((type) => (
                  <option key={type.valeur} value={type.valeur}>
                    {type.libelle}
                  </option>
                ))}
              </select>
            )}
          </Champ>

          {entreprise ? (
            <Champ libelle="Statut de la relation">
              <select
                value={valeurs.statutRelation}
                onChange={(evenement) => modifier('statutRelation', evenement.target.value)}
                className={CLASSE_CHAMP}
              >
                {statutsRelation.map((statut) => (
                  <option key={statut.valeur} value={statut.valeur}>
                    {statut.libelle}
                  </option>
                ))}
              </select>
            </Champ>
          ) : (
            modification && (
              <Champ libelle="Type de partenaire">
                <select
                  value={valeurs.nature}
                  onChange={(evenement) => modifier('nature', evenement.target.value)}
                  className={CLASSE_CHAMP}
                >
                  {typesPartenaire.map((type) => (
                    <option key={type.valeur} value={type.valeur}>
                      {type.libelle}
                    </option>
                  ))}
                </select>
              </Champ>
            )
          )}
        </Section>

        <Section titre="Identification">
          <Champ libelle={entreprise ? 'Raison sociale' : 'Nom'}>
            <input
              value={valeurs.nom}
              onChange={(evenement) => modifier('nom', evenement.target.value)}
              className={CLASSE_CHAMP}
            />
          </Champ>

          {entreprise ? (
            <Champ libelle="SIRET">
              <input
                value={valeurs.siret}
                onChange={(evenement) => modifier('siret', evenement.target.value)}
                placeholder="14 chiffres"
                className={CLASSE_CHAMP}
              />
              {/* Le SIREN n'est pas saisi : ce sont les neuf premiers chiffres
                  du SIRET, et le ressaisir ouvrirait la porte à deux valeurs
                  qui se contredisent. */}
              <p className="mt-1 text-[11px] text-codialis-gray">
                SIREN déduit :{' '}
                <span className="font-mono">{sansEspaces(valeurs.siret).slice(0, 9) || '—'}</span>
              </p>
            </Champ>
          ) : (
            <Champ libelle="Numéro RNA (association déclarée)">
              <input
                value={valeurs.rna}
                onChange={(evenement) => modifier('rna', evenement.target.value)}
                placeholder="W974001234"
                className={CLASSE_CHAMP}
              />
            </Champ>
          )}

          <Champ libelle={entreprise ? 'Secteur d’activité' : 'Domaine d’intervention'}>
            <input
              list="organisation-activites"
              value={valeurs.activite}
              onChange={(evenement) => modifier('activite', evenement.target.value)}
              className={CLASSE_CHAMP}
            />
            <datalist id="organisation-activites">
              {secteurs.map((valeur) => (
                <option key={valeur} value={valeur} />
              ))}
            </datalist>
          </Champ>

          <Champ libelle="Site internet">
            <input
              value={valeurs.siteWeb}
              onChange={(evenement) => modifier('siteWeb', evenement.target.value)}
              placeholder="https://"
              className={CLASSE_CHAMP}
            />
          </Champ>
        </Section>

        <Section
          titre={
            entreprise ? 'Adresse de l’établissement d’exécution du contrat' : 'Coordonnées'
          }
        >
          {entreprise ? (
            <>
              {/* Le CERFA sépare le numéro de la voie : le ressaisir en une
                  seule ligne oblige à le redécouper au moment de générer le
                  formulaire, sur une adresse qu'on ne peut plus vérifier. */}
              <Champ libelle="N° de voie de l’établissement">
                <input
                  value={valeurs.numeroVoie}
                  onChange={(evenement) => modifier('numeroVoie', evenement.target.value)}
                  className={CLASSE_CHAMP}
                />
              </Champ>
              <Champ libelle="Voie de l’établissement">
                <input
                  value={valeurs.voie}
                  onChange={(evenement) => modifier('voie', evenement.target.value)}
                  className={CLASSE_CHAMP}
                />
              </Champ>
              <div className="md:col-span-2 min-w-0">
                <Champ libelle="Complément d’adresse">
                  <input
                    value={valeurs.complementAdresse}
                    onChange={(evenement) =>
                      modifier('complementAdresse', evenement.target.value)
                    }
                    className={CLASSE_CHAMP}
                  />
                </Champ>
              </div>
            </>
          ) : (
            <div className="md:col-span-2 min-w-0">
              <Champ libelle="Adresse">
                <input
                  value={valeurs.adresse}
                  onChange={(evenement) => modifier('adresse', evenement.target.value)}
                  className={CLASSE_CHAMP}
                />
              </Champ>
            </div>
          )}
          <Champ libelle={entreprise ? 'Code postal' : 'Code postal'}>
            <input
              value={valeurs.codePostal}
              onChange={(evenement) => modifier('codePostal', evenement.target.value)}
              className={CLASSE_CHAMP}
            />
          </Champ>
          <Champ libelle={entreprise ? 'Commune' : 'Ville'}>
            <input
              value={valeurs.ville}
              onChange={(evenement) => modifier('ville', evenement.target.value)}
              className={CLASSE_CHAMP}
            />
          </Champ>
          <Champ libelle="Téléphone">
            <input
              value={valeurs.telephone}
              onChange={(evenement) => modifier('telephone', evenement.target.value)}
              className={CLASSE_CHAMP}
            />
          </Champ>
          <Champ libelle="Adresse e-mail">
            <input
              type="email"
              value={valeurs.email}
              onChange={(evenement) => modifier('email', evenement.target.value)}
              className={CLASSE_CHAMP}
            />
          </Champ>
          <Champ libelle="Personne de contact">
            <input
              value={valeurs.contact}
              onChange={(evenement) => modifier('contact', evenement.target.value)}
              className={CLASSE_CHAMP}
            />
          </Champ>
        </Section>

        {entreprise && (
          <Section titre="Employeur">
            {/* Case à cocher du CERFA. Le secteur public entraîne l'adhésion de
                l'apprenti au régime spécifique d'assurance chômage : l'écran le
                rappelle plutôt que de laisser la conséquence implicite. */}
            <Champ libelle="Employeur privé ou public">
              <select
                value={valeurs.secteurEmployeur}
                onChange={(evenement) =>
                  modifier('secteurEmployeur', evenement.target.value as SecteurEmployeur)
                }
                className={CLASSE_CHAMP}
              >
                <option value="PRIVE">Employeur privé</option>
                <option value="PUBLIC">Employeur public</option>
              </select>
              {valeurs.secteurEmployeur === 'PUBLIC' && (
                <p className="mt-1 text-[11px] text-amber-700">
                  Secteur public : adhésion de l’apprenti(e) au régime spécifique d’assurance
                  chômage à prévoir au contrat.
                </p>
              )}
            </Champ>
            <Champ libelle="Employeur spécifique">
              <input
                list="organisation-employeurs-specifiques"
                value={valeurs.employeurSpecifique}
                onChange={(evenement) => modifier('employeurSpecifique', evenement.target.value)}
                placeholder="Groupement d’employeurs, travail temporaire…"
                className={CLASSE_CHAMP}
              />
              <datalist id="organisation-employeurs-specifiques">
                {employeursSpecifiques.map((valeur) => (
                  <option key={valeur} value={valeur} />
                ))}
              </datalist>
            </Champ>
            <Champ libelle="Gérant">
              <input
                value={valeurs.gerant}
                onChange={(evenement) => modifier('gerant', evenement.target.value)}
                className={CLASSE_CHAMP}
              />
            </Champ>
            <Champ libelle="Effectif total salariés">
              <input
                inputMode="numeric"
                value={valeurs.effectif}
                onChange={(evenement) => modifier('effectif', evenement.target.value)}
                className={CLASSE_CHAMP}
              />
            </Champ>
            <Champ libelle="OPCO">
              <input
                value={valeurs.opco}
                onChange={(evenement) => modifier('opco', evenement.target.value)}
                className={CLASSE_CHAMP}
              />
            </Champ>
            <Champ libelle="Convention collective (IDCC)">
              <input
                value={valeurs.idcc}
                onChange={(evenement) => modifier('idcc', evenement.target.value)}
                placeholder="4 chiffres"
                className={CLASSE_CHAMP}
              />
            </Champ>
            {/* Les trois champs de la fiche employeur (CDC §4.8) : sans eux, le
                dossier affichait des tirets qu'aucun écran ne permettait de
                remplir, et le CERFA partait incomplet. */}
            <Champ libelle="Type d’employeur">
              <input
                list="organisation-types-employeur"
                value={valeurs.typeEmployeur}
                onChange={(evenement) => modifier('typeEmployeur', evenement.target.value)}
                placeholder="Entreprise privée, association…"
                className={CLASSE_CHAMP}
              />
              <datalist id="organisation-types-employeur">
                {typesEmployeur.map((valeur) => (
                  <option key={valeur} value={valeur} />
                ))}
              </datalist>
            </Champ>
            <Champ libelle="Code activité de l’entreprise (APE)">
              <input
                value={valeurs.codeNaf}
                onChange={(evenement) => modifier('codeNaf', evenement.target.value)}
                placeholder="1071C"
                className={CLASSE_CHAMP}
              />
            </Champ>
            <Champ libelle="Caisse de retraite complémentaire">
              <input
                value={valeurs.caisseRetraite}
                onChange={(evenement) => modifier('caisseRetraite', evenement.target.value)}
                className={CLASSE_CHAMP}
              />
            </Champ>
          </Section>
        )}

        {entreprise && (
          <Section titre="Maîtres d’apprentissage">
            <SaisieMaitresApprentissage
              existants={modification ? maitresExistants : []}
              lignes={valeurs.maitres}
              onChange={(maitres) => modifier('maitres', maitres)}
            />
          </Section>
        )}

        <Section titre="Rattachement">
          <div className="md:col-span-2 min-w-0">
            <Champ libelle="Antennes de rattachement" groupe>
              <div className="flex flex-wrap gap-2">
                {sitesDisponibles.map((code) => {
                  const actif = valeurs.sites.includes(code);
                  return (
                    <button
                      key={code}
                      type="button"
                      onClick={() => basculerSite(code)}
                      aria-pressed={actif}
                      className={`min-h-[44px] rounded-lg border px-3 text-xs font-bold transition ${
                        actif
                          ? 'border-codialis-mint bg-codialis-sand text-codialis-navy'
                          : 'border-gray-200 bg-gray-50 text-codialis-gray'
                      }`}
                    >
                      {code}
                    </button>
                  );
                })}
              </div>
            </Champ>
          </div>
        </Section>

        {erreur && <MessageErreur message={erreur} />}
        {problemes.length > 0 && (
          <ul className="space-y-1 rounded-xl bg-amber-50 px-4 py-3 text-xs text-amber-800">
            {problemes.map((probleme) => (
              <li key={probleme}>{probleme}</li>
            ))}
          </ul>
        )}

        {/* Barre d'actions collante, comme sur le formulaire d'inscription :
            la fiche fait plus de deux mille pixels sur téléphone, et
            « Enregistrer » se trouvait tout en bas. */}
        <div className="sticky bottom-0 -mx-4 flex flex-wrap justify-end gap-2 border-t border-gray-100 bg-white px-4 pb-1 pt-4 sm:-mx-6 sm:px-6">
          <BoutonSecondaire onClick={onFermer} disabled={enCours}>
            Annuler
          </BoutonSecondaire>
          <BoutonMint type="submit" disabled={enCours || problemes.length > 0}>
            {enCours ? 'Enregistrement…' : 'Enregistrer'}
          </BoutonMint>
        </div>
      </form>
    </Modale>
  );
}

function Section({ titre, children }: { titre: string; children: React.ReactNode }): JSX.Element {
  return (
    <fieldset>
      <legend className="mb-2 text-[11px] font-bold uppercase tracking-wider text-codialis-gray">
        {titre}
      </legend>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">{children}</div>
    </fieldset>
  );
}
